import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface HealthRow { readonly component: string; readonly status: string; readonly healthScore: string }
interface SystemStatus {
  readonly health: HealthRow[];
  readonly api: { readonly healthy: number; readonly degraded: number; readonly down: number };
  readonly database: { readonly name: string; readonly status: string } | null;
  readonly jobs: { readonly running: number; readonly failed: number; readonly retrying: number };
  readonly incidents: { readonly open: number; readonly escalated: number };
  readonly deployments: { readonly lastVersion: string | null; readonly lastStatus: string | null };
  readonly security: { readonly openAlerts: number };
  readonly storage: { readonly breached: number };
  readonly resources: { readonly cpuPercent: number; readonly memoryPercent: number; readonly loadAverage: number };
  readonly network: { readonly status: string };
  readonly activeUsers: number;
  readonly errorSummary: { readonly last24h: number };
}

export function M19SystemDashboardPage(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { t } = useTranslation();
  const { token } = useAuth();
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: SystemStatus }>('/v1/platform/system/status', { token })
      .then((r) => { if (alive) setStatus(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  if (error) return <p role="alert">{error}</p>;
  if (!status) return <p>{t('m19.system.loading')}</p>;
  return (
    <main aria-label="system-dashboard">
      <h1>{t('m19.system.title')}</h1>
      <section aria-label="api-health">
        <h2>{t('m19.system.apiHealth')}</h2>
        <p>{t('m19.system.apiHealthLine', { healthy: status.api.healthy, degraded: status.api.degraded, down: status.api.down })}</p>
      </section>
      <section aria-label="resources">
        <h2>{t('m19.system.resources')}</h2>
        <p>{t('m19.system.resourcesLine', { cpu: status.resources.cpuPercent, memory: status.resources.memoryPercent, load: status.resources.loadAverage })}</p>
      </section>
      <section aria-label="network">
        <h2>{t('m19.system.network')}</h2>
        <p>{status.network.status}</p>
      </section>
      <section aria-label="active-users">
        <h2>{t('m19.system.activeUsers')}</h2>
        <p>{status.activeUsers}</p>
      </section>
      <section aria-label="error-summary">
        <h2>{t('m19.system.errorSummary')}</h2>
        <p>{t('m19.system.errorSummaryLine', { count: status.errorSummary.last24h })}</p>
      </section>
      <section aria-label="jobs">
        <h2>{t('m19.system.jobs')}</h2>
        <p>{t('m19.system.jobsLine', { running: status.jobs.running, failed: status.jobs.failed, retrying: status.jobs.retrying })}</p>
      </section>
      <section aria-label="incidents">
        <h2>{t('m19.system.incidents')}</h2>
        <p>{t('m19.system.incidentsLine', { open: status.incidents.open, escalated: status.incidents.escalated })}</p>
      </section>
      <section aria-label="security">
        <h2>{t('m19.system.security')}</h2>
        <p>{t('m19.system.securityLine', { count: status.security.openAlerts })}</p>
      </section>
      <section aria-label="storage">
        <h2>{t('m19.system.storage')}</h2>
        <p>{t('m19.system.storageLine', { count: status.storage.breached })}</p>
      </section>
      <section aria-label="deployments">
        <h2>{t('m19.system.lastDeployment')}</h2>
        <p>{t('m19.system.deploymentLine', { version: status.deployments.lastVersion ?? '—', status: status.deployments.lastStatus ?? '—' })}</p>
      </section>
      <table aria-label="component-health">
        <thead><tr><th>{t('m19.system.colComponent')}</th><th>{t('m19.system.colStatus')}</th><th>{t('m19.system.colScore')}</th></tr></thead>
        <tbody>
          {status.health.map((h) => (
            <tr key={h.component}>
              <td>{h.component}</td>
              <td>{h.status}</td>
              <td>{decimal(h.healthScore)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
